import { ArrowUpDown, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { UPLOAD_STATUSES } from './constants';
import { deriveMetadataStatus } from './utils';
import { formatFullName } from 'shared/utils';

const STATUS_VARIANTS = {
  [UPLOAD_STATUSES.CREATED]: 'secondary',
  [UPLOAD_STATUSES.MAPPED]: 'secondary',
  [UPLOAD_STATUSES.PROCESSING]: 'default',
  [UPLOAD_STATUSES.ZIP_UPLOAD_COMPLETE]: 'secondary',
  [UPLOAD_STATUSES.EXTRACTING_METADATA]: 'default',
  [UPLOAD_STATUSES.COMPLETED]: 'default',
  [UPLOAD_STATUSES.FAILED]: 'destructive',
};

const STATUS_COLORS = {
  [UPLOAD_STATUSES.CREATED]: 'bg-blue-500',
  [UPLOAD_STATUSES.MAPPED]: 'bg-indigo-500',
  [UPLOAD_STATUSES.PROCESSING]: 'bg-yellow-500',
  [UPLOAD_STATUSES.ZIP_UPLOAD_COMPLETE]: 'bg-green-500',
  [UPLOAD_STATUSES.EXTRACTING_METADATA]: 'bg-blue-500',
  [UPLOAD_STATUSES.COMPLETED]: 'bg-green-500',
  [UPLOAD_STATUSES.FAILED]: 'bg-red-500',
};

export const createUploadColumns = workspaces => [
  {
    id: 'expander',
    header: () => null,
    cell: ({ row }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => row.toggleExpanded()}
          className="h-8 w-8 p-0">
          {row.getIsExpanded() ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </Button>
      );
    },
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: '_id',
    header: 'Upload ID',
    cell: ({ row }) => {
      const uploadId = row.getValue('_id');
      return <div className="font-mono text-xs">{uploadId || 'N/A'}</div>;
    },
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    accessorKey: 'workspaceId',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="h-auto p-0 font-semibold hover:bg-transparent">
          Workspace
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const workspaceData = row.getValue('workspaceId');
      const workspaceId = workspaceData?._id || workspaceData?.id || workspaceData;
      const workspace = workspaces.find(w => w.id === workspaceId);
      return workspace?.name || 'Unknown Workspace';
    },
    filterFn: (row, id, value) => {
      if (!value) return true;
      const workspaceData = row.getValue('workspaceId');
      const workspaceId = workspaceData?._id || workspaceData?.id || workspaceData;
      return workspaceId === value;
    },
  },
  {
    accessorKey: 'status',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="h-auto p-0 font-semibold hover:bg-transparent">
          Status
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const upload = row.original;
      const { displayStatus, statusText } = deriveMetadataStatus({
        status: upload.status,
        skipMetadataExtraction: upload.skipMetadataExtraction,
      });

      return (
        <Badge
          variant={STATUS_VARIANTS[displayStatus] || 'secondary'}
          className={cn(STATUS_COLORS[displayStatus], 'text-white pointer-events-none')}>
          {statusText}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'tags',
    header: 'Tags',
    cell: ({ row }) => {
      const tags = row.getValue('tags');
      if (!tags?.length) {
        return <span className="text-muted-foreground">—</span>;
      }
      return (
        <div className="flex items-center gap-1.5 flex-wrap">
          {tags.map(tag => (
            <Badge key={tag} variant="outline" className="text-xs pointer-events-none">
              {tag}
            </Badge>
          ))}
        </div>
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: 'createdBy',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="h-auto p-0 font-semibold hover:bg-transparent">
          Created By
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const createdBy = row.getValue('createdBy');
      if (!createdBy) {
        return <div className="text-muted-foreground">System</div>;
      }
      const user = createdBy;
      const displayName = formatFullName(user);
      return (
        <div className="truncate max-w-[200px]" title={displayName}>
          {displayName}
        </div>
      );
    },
    filterFn: (row, id, value) => {
      if (!value) return true;
      const createdBy = row.getValue('createdBy');
      if (!createdBy && value === 'System') return true;
      if (!createdBy) return false;

      const user = createdBy;
      const displayName = formatFullName(user);
      return displayName === value;
    },
  },
  {
    accessorKey: 'totalFiles',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="h-auto p-0 font-semibold hover:bg-transparent">
          Files
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      return row.getValue('totalFiles') || 0;
    },
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="h-auto p-0 font-semibold hover:bg-transparent">
          Created
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      return format(new Date(row.getValue('createdAt')), 'MMM dd, yyyy HH:mm');
    },
  },
];
